import {
    DependencyList,
    useCallback,
    useEffect,
    useLayoutEffect,
    useRef,
    useState,
} from "react";
import { useMemoByKeys } from "@hooks/useMemoByKeys";

interface UseDynamicTopOffsetOptions {
    /**
     * Reuse an existing ref instead of creating a new one — e.g. `ViewTable`
     * measures its own already-managed scroll container rather than asking
     * callers to wire up a separate ref.
     */
    containerRef?: React.RefObject<HTMLDivElement | null>;
    /**
     * Values that, when changed, force a re-measurement in addition to the
     * `ResizeObserver`-driven updates. Needed for layout shifts that move the
     * container without resizing it — see the note below.
     */
    recomputeOn?: DependencyList;
}

/**
 * Measures the live viewport top offset of a container via
 * `getBoundingClientRect`, re-measuring on resize. Use this instead of
 * hardcoding a sum of surrounding element heights for `ViewTable`'s
 * `fixedTopOffsetPx` / `DetailsAside` — it stays correct as surrounding
 * layout changes.
 *
 * The container's own height is typically pinned to `100vh - topOffset` (see
 * `ViewTable`'s `hasFixedTopOffsetPx` styles), so it won't naturally resize
 * just because chrome *above* it appeared/disappeared elsewhere in the DOM —
 * that only shifts its position. Pass `recomputeOn` with values that signal
 * such position-only layout shifts (e.g. a product tip banner) to force a
 * re-measure when the `ResizeObserver` alone wouldn't catch it.
 */
export function useDynamicTopOffset({
    containerRef: externalContainerRef,
    recomputeOn = [],
}: UseDynamicTopOffsetOptions = {}) {
    const [topOffset, setTopOffset] = useState(0);
    const [hasMeasured, setHasMeasured] = useState(false);
    const internalContainerRef = useRef<HTMLDivElement>(null);
    const containerRef = externalContainerRef ?? internalContainerRef;

    const updateTopOffset = useCallback((container: HTMLDivElement | null) => {
        if (!container) {
            return;
        }
        const rect = container.getBoundingClientRect();
        setTopOffset(rect.top);
        setHasMeasured(true);
    }, []);

    useLayoutEffect(() => {
        const container = containerRef.current;
        if (!container) {
            return;
        }

        const updateFromContainer = () => updateTopOffset(containerRef.current);
        updateFromContainer();

        const resizeObserver = new ResizeObserver(updateFromContainer);
        resizeObserver.observe(container);
        window.addEventListener("resize", updateFromContainer);

        return () => {
            resizeObserver.disconnect();
            window.removeEventListener("resize", updateFromContainer);
        };
    }, [containerRef, updateTopOffset]);

    // The layout shifts that motivate a manual `recomputeOn` trigger (e.g. a
    // fullscreen toggle hiding the app header, or a nav/detail-panel collapse)
    // are applied by sibling/ancestor effects that commit in a different pass
    // than this one, so measuring synchronously here would read the stale
    // pre-shift layout. Defer the re-measure to the next animation frame, once
    // the DOM has settled.
    const remeasureTopOffset = useMemoByKeys(
        () => () => updateTopOffset(containerRef.current),
        [updateTopOffset, containerRef, ...recomputeOn],
    );
    useEffect(() => {
        const frameId = requestAnimationFrame(remeasureTopOffset);
        return () => cancelAnimationFrame(frameId);
    }, [remeasureTopOffset]);

    return { topOffset, hasMeasured, containerRef };
}
